/**
 * Emerald Books — CRM (Tasks + Tickets). Mobile-first.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Plus, CheckCircle2, MessageSquare, AlertCircle, Filter, X,
  Clock, User, Tag, ArrowUpRight, Ticket, ListChecks, Mail, Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, errMsg, unwrapList } from './api';

const PRIORITIES = [
  { v: 'low', label: 'Low', cls: 'bg-slate-100 text-slate-700' },
  { v: 'normal', label: 'Normal', cls: 'bg-emerald-50 text-emerald-700' },
  { v: 'high', label: 'High', cls: 'bg-amber-50 text-amber-700' },
  { v: 'urgent', label: 'Urgent', cls: 'bg-rose-50 text-rose-700' },
];
const STATUS_CLS = {
  open: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  done: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500',
  pending: 'bg-amber-50 text-amber-700',
  resolved: 'bg-emerald-50 text-emerald-700',
  closed: 'bg-slate-100 text-slate-500',
};
const PriorityPill = ({ v }) => {
  const p = PRIORITIES.find((x) => x.v === v) || PRIORITIES[1];
  return <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${p.cls}`}>{p.label}</span>;
};
const StatusPill = ({ v }) => (
  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${STATUS_CLS[v] || 'bg-slate-100 text-slate-600'}`}>
    {(v || '').replace('_', ' ')}
  </span>
);

/* -------------------------------------------------------------------------- */
/* Assignee picker — dropdown of employees + fallback email input             */
/* -------------------------------------------------------------------------- */
const AssigneePicker = ({ employees, mode, setMode, employeeId, setEmployeeId, email, setEmail, testidPrefix = 'assignee' }) => (
  <div className="space-y-1">
    <div className="flex gap-2 text-xs">
      <button type="button" onClick={() => setMode('employee')} data-testid={`${testidPrefix}-mode-employee`}
        className={`px-3 py-1 rounded ${mode === 'employee' ? 'bg-[#0f5c3d] text-white' : 'bg-slate-100 text-slate-600'}`}>
        <Users className="w-3 h-3 inline mr-1" /> Employee
      </button>
      <button type="button" onClick={() => setMode('email')} data-testid={`${testidPrefix}-mode-email`}
        className={`px-3 py-1 rounded ${mode === 'email' ? 'bg-[#0f5c3d] text-white' : 'bg-slate-100 text-slate-600'}`}>
        <Mail className="w-3 h-3 inline mr-1" /> External email
      </button>
    </div>
    {mode === 'employee' ? (
      <select required value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
        data-testid={`${testidPrefix}-employee-select`}
        className="w-full px-3 py-2 border rounded text-sm">
        <option value="">Select an employee…</option>
        {employees.map((e) => (
          <option key={e.employee_id} value={e.employee_id}>
            {e.first_name} {e.last_name} · {e.employee_code}{e.work_email ? ` · ${e.work_email}` : ''}
          </option>
        ))}
      </select>
    ) : (
      <Input type="email" required placeholder="name@company.com"
        value={email} onChange={(e) => setEmail(e.target.value)}
        data-testid={`${testidPrefix}-email-input`} />
    )}
  </div>
);

/* -------------------------------------------------------------------------- */
/* Tasks                                                                       */
/* -------------------------------------------------------------------------- */
export const CRMTasksPage = () => {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('mine');       // mine|open|all|done
  const [assigneeMode, setAssigneeMode] = useState('employee');
  const [assigneeEmployeeId, setAssigneeEmployeeId] = useState('');
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [form, setForm] = useState({
    title: '', description: '', priority: 'normal',
    due_date: '', tags: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'mine') params.mine = true;
      else if (filter === 'open') params.status = 'open';
      else if (filter === 'done') params.status = 'done';
      const [tasksR, empR] = await Promise.all([
        booksClient.get('/crm/tasks', { params }),
        booksClient.get('/hr/employees', { params: { status: 'active' } }),
      ]);
      setItems(unwrapList(tasksR));
      setEmployees(unwrapList(empR));
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    if (assigneeMode === 'employee' && !assigneeEmployeeId) {
      toast.error('Please select an employee to assign'); return;
    }
    if (assigneeMode === 'email' && !assigneeEmail) {
      toast.error('Please enter an email address'); return;
    }
    try {
      await booksClient.post('/crm/tasks', {
        title: form.title,
        description: form.description,
        priority: form.priority,
        assignee_employee_id: assigneeMode === 'employee' ? assigneeEmployeeId : undefined,
        assignee_email: assigneeMode === 'email' ? assigneeEmail : undefined,
        due_date: form.due_date || undefined,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      });
      toast.success('Task created');
      setShowForm(false);
      setForm({ title: '', description: '', priority: 'normal', due_date: '', tags: '' });
      setAssigneeEmployeeId(''); setAssigneeEmail('');
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const complete = async (id) => {
    try {
      await booksClient.post(`/crm/tasks/${id}/complete`);
      toast.success('Task completed');
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-[#0b1a2e]">Tasks</h1>
          <p className="text-sm text-slate-500">Assign, track and close internal work</p>
        </div>
        <Button data-testid="crm-task-new-btn" onClick={() => setShowForm(true)} className="bg-[#0f5c3d] hover:bg-[#0d4d33]">
          <Plus className="w-4 h-4 mr-1" /> New task
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { v: 'mine', label: 'My tasks' },
          { v: 'open', label: 'All open' },
          { v: 'done', label: 'Done' },
          { v: 'all', label: 'All' },
        ].map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)}
            data-testid={`crm-task-filter-${f.v}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === f.v ? 'bg-[#0b1a2e] text-white' : 'bg-white text-slate-600 border'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white p-4 rounded-lg border space-y-3 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-800">New task</div>
            <button type="button" onClick={() => setShowForm(false)}><X className="w-4 h-4 text-slate-400" /></button>
          </div>
          <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="crm-task-title-input" />
          <textarea className="w-full px-3 py-2 border rounded text-sm" rows={3}
            placeholder="Description" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">Assign to *</div>
            <AssigneePicker employees={employees} mode={assigneeMode} setMode={setAssigneeMode}
              employeeId={assigneeEmployeeId} setEmployeeId={setAssigneeEmployeeId}
              email={assigneeEmail} setEmail={setAssigneeEmail} testidPrefix="crm-task-assignee" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="px-3 py-2 border rounded text-sm">
              {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
            </select>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <Input placeholder="Tags, comma separated" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          <Button type="submit" data-testid="crm-task-submit-btn" className="w-full bg-[#0f5c3d] hover:bg-[#0d4d33]">Create</Button>
        </form>
      )}

      {loading ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : (
        <div className="space-y-2" data-testid="crm-task-list">
          {items.length === 0 && <div className="text-center text-slate-400 py-12">No tasks</div>}
          {items.map((t) => (
            <div key={t.task_id} className="bg-white rounded-lg border p-3 hover:shadow-sm transition">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <PriorityPill v={t.priority} />
                    <StatusPill v={t.status} />
                    {(t.tags || []).map((tag) => (
                      <span key={tag} className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="mt-1.5 font-semibold text-slate-800">{t.title}</div>
                  {t.description && <div className="text-sm text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>}
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                    {t.assignee_name && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{t.assignee_name}</span>}
                    {t.assignee_email && !t.assignee_employee_id && <span className="inline-flex items-center gap-1 text-slate-400"><Mail className="w-3 h-3" />{t.assignee_email}</span>}
                    {t.due_date && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{t.due_date}</span>}
                    {t.assigner_name && <span className="text-slate-400">by {t.assigner_name}</span>}
                  </div>
                </div>
                {t.status !== 'done' && t.status !== 'cancelled' && (
                  <button onClick={() => complete(t.task_id)} data-testid={`crm-task-complete-${t.task_id}`}
                    className="p-2 rounded hover:bg-emerald-50 text-emerald-600" title="Mark done">
                    <CheckCircle2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Tickets                                                                     */
/* -------------------------------------------------------------------------- */
const CATEGORIES = [
  { v: 'hr', label: 'HR' }, { v: 'it', label: 'IT' },
  { v: 'finance', label: 'Finance' }, { v: 'facilities', label: 'Facilities' },
  { v: 'other', label: 'Other' },
];

export const CRMTicketsPage = () => {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('mine');
  const [assigneeMode, setAssigneeMode] = useState('employee');
  const [assigneeEmployeeId, setAssigneeEmployeeId] = useState('');
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [form, setForm] = useState({
    subject: '', description: '', category: 'hr', priority: 'normal',
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'mine') params.mine = true;
      else if (filter === 'open') params.status = 'open';
      else if (['hr', 'it', 'finance'].includes(filter)) params.category = filter;
      const [tR, eR] = await Promise.all([
        booksClient.get('/crm/tickets', { params }),
        booksClient.get('/hr/employees', { params: { status: 'active' } }),
      ]);
      setItems(unwrapList(tR));
      setEmployees(unwrapList(eR));
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await booksClient.post('/crm/tickets', {
        ...form,
        assigned_employee_id: assigneeMode === 'employee' && assigneeEmployeeId ? assigneeEmployeeId : undefined,
        assigned_email: assigneeMode === 'email' && assigneeEmail ? assigneeEmail : undefined,
      });
      toast.success('Ticket raised');
      setShowForm(false);
      setForm({ subject: '', description: '', category: 'hr', priority: 'normal' });
      setAssigneeEmployeeId(''); setAssigneeEmail('');
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const resolve = async (id) => {
    try {
      await booksClient.post(`/crm/tickets/${id}/resolve`);
      toast.success('Ticket resolved');
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-[#0b1a2e]">Tickets</h1>
          <p className="text-sm text-slate-500">Raise & track internal support requests</p>
        </div>
        <Button data-testid="crm-ticket-new-btn" onClick={() => setShowForm(true)} className="bg-[#0f5c3d] hover:bg-[#0d4d33]">
          <Plus className="w-4 h-4 mr-1" /> Raise ticket
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { v: 'mine', label: 'My tickets' },
          { v: 'open', label: 'All open' },
          { v: 'hr', label: 'HR' },
          { v: 'it', label: 'IT' },
          { v: 'finance', label: 'Finance' },
          { v: 'all', label: 'All' },
        ].map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === f.v ? 'bg-[#0b1a2e] text-white' : 'bg-white text-slate-600 border'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white p-4 rounded-lg border space-y-3 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-800">Raise a ticket</div>
            <button type="button" onClick={() => setShowForm(false)}><X className="w-4 h-4 text-slate-400" /></button>
          </div>
          <Input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="crm-ticket-subject-input" />
          <textarea className="w-full px-3 py-2 border rounded text-sm" rows={3}
            placeholder="Describe the issue…" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} required />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="px-3 py-2 border rounded text-sm">
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="px-3 py-2 border rounded text-sm">
              {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">Route to (optional)</div>
            <AssigneePicker employees={employees} mode={assigneeMode} setMode={setAssigneeMode}
              employeeId={assigneeEmployeeId} setEmployeeId={setAssigneeEmployeeId}
              email={assigneeEmail} setEmail={setAssigneeEmail} testidPrefix="crm-ticket-assignee" />
          </div>
          <Button type="submit" data-testid="crm-ticket-submit-btn" className="w-full bg-[#0f5c3d] hover:bg-[#0d4d33]">Submit</Button>
        </form>
      )}

      {loading ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : (
        <div className="space-y-2" data-testid="crm-ticket-list">
          {items.length === 0 && <div className="text-center text-slate-400 py-12">No tickets</div>}
          {items.map((t) => (
            <div key={t.ticket_id} className="bg-white rounded-lg border p-3 hover:shadow-sm transition">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono text-slate-400">{t.ticket_no}</span>
                    <PriorityPill v={t.priority} />
                    <StatusPill v={t.status} />
                    <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                      {t.category}
                    </span>
                  </div>
                  <div className="mt-1.5 font-semibold text-slate-800">{t.subject}</div>
                  {t.description && <div className="text-sm text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>}
                  <div className="text-xs text-slate-400 mt-1">by {t.requester_name}</div>
                </div>
                {t.status !== 'resolved' && t.status !== 'closed' && (
                  <button onClick={() => resolve(t.ticket_id)}
                    className="p-2 rounded hover:bg-emerald-50 text-emerald-600" title="Resolve">
                    <CheckCircle2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
